
import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  onAuthStateChanged, 
  User, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp, updateDoc, arrayUnion } from 'firebase/firestore';
import { UserProfile } from '../types';

const firebaseConfig = {
  apiKey: "AIzaSyDWk7PMWCxhzqNqr9BHvn_efUarnvKdXQo",
  authDomain: "afamiliaobpc.firebaseapp.com",
  projectId: "afamiliaobpc",
  storageBucket: "afamiliaobpc.firebasestorage.app",
  messagingSenderId: "547294496485",
  appId: "1:547294496485:web:9b872d9460c868bd1f360c",
  measurementId: "G-MPG0X7GKRQ"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Forçar persistência local para garantir que o SSO entre subdomínios funcione
setPersistence(auth, browserLocalPersistence);

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  signIn: () => Promise<void>;
  signInEmail: (email: string, pass: string) => Promise<void>;
  signUpEmail: (email: string, pass: string, name: string, church: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // O onAuthStateChanged é o coração do SSO. 
    // Ele detectará se o usuário já logou no site principal (mesmo projeto Firebase).
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userRef);
          
          const currentMethod = firebaseUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email';

          if (userDoc.exists()) {
            const data = userDoc.data() as UserProfile;
            setProfile(data);
            
            await updateDoc(userRef, { 
              lastLogin: serverTimestamp(),
              loginMethods: arrayUnion(currentMethod),
              lastAppUsed: 'calendar'
            });
          } else {
            const newProfile: UserProfile = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: firebaseUser.displayName || '',
              appRoles: { 'calendar': 'Membro' },
              church: 'Monte Castelo'
            };
            
            await setDoc(userRef, {
              ...newProfile,
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp(),
              loginMethods: [currentMethod],
              sourceApp: 'calendar'
            });
            setProfile(newProfile);
          }
        } catch (error) {
          console.error("Erro na governança SSO:", error);
        }
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      console.error("Erro no login Google:", error);
      throw error;
    }
  };

  const signInEmail = async (email: string, pass: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, pass);
    } catch (error: any) {
      throw error;
    }
  };

  const signUpEmail = async (email: string, pass: string, name: string, church: string) => {
    const credential = await createUserWithEmailAndPassword(auth, email, pass);
    await updateProfile(credential.user, { displayName: name });
    
    const userRef = doc(db, 'users', credential.user.uid);
    const newProfile: UserProfile = {
      uid: credential.user.uid,
      email: email,
      name: name,
      church: church as any,
      appRoles: { 'calendar': 'Membro' }
    };

    await setDoc(userRef, {
      ...newProfile,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      loginMethods: ['email'],
      sourceApp: 'calendar'
    });
  };

  const logout = async () => {
    await signOut(auth);
  };

  const getToken = async () => {
    if (!auth.currentUser) return null;
    return await auth.currentUser.getIdToken();
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signInEmail, signUpEmail, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  return context;
};

export { db };
